import { useEffect, useState } from "react";
import { ClipboardCheck, Check, X, Paperclip, Timer, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage, openStoredFile, formatTanggalID, formatRupiah } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ApprovalChain } from "@/components/ApprovalChain";

function LeaveCard({ r, actions }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`approval-card-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={r.jenis} />
            <StatusBadge status={r.status} />
            {r.jenis === "Izin" && r.izin_classification ? (
              <span
                className="text-[10px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"
                data-testid={`approval-klasifikasi-${r.id}`}
              >
                {r.izin_classification}
                {r.izin_subtype ? ` • ${r.izin_subtype}` : ""}
              </span>
            ) : null}
            {r.benefit_deduction_status ? (
              <span
                className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${
                  r.benefit_deduction_status === "Potong Tunjangan"
                    ? "text-rose-700 bg-rose-50 border-rose-200"
                    : "text-emerald-700 bg-emerald-50 border-emerald-200"
                }`}
                data-testid={`approval-benefit-${r.id}`}
              >
                {r.benefit_deduction_status}
              </span>
            ) : null}
          </div>
          <div className="mt-2 font-semibold text-slate-900">
            {r.nama} <span className="text-xs font-normal text-slate-500">({r.npp})</span>
          </div>
          <div className="text-sm text-slate-700 mt-0.5">
            {formatTanggalID(r.tanggal_mulai)}
            {r.tanggal_selesai !== r.tanggal_mulai ? ` — ${formatTanggalID(r.tanggal_selesai)}` : ""}
            <span className="ml-1.5 text-xs text-slate-500">({r.jumlah_hari} hari)</span>
          </div>
          <div className="text-sm text-slate-600 mt-1">{r.alasan}</div>
          {r.lampiran_path ? (
            <button
              onClick={() => openStoredFile(r.lampiran_path).catch(() => toast.error("Gagal membuka lampiran"))}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-800 hover:text-teal-900"
              data-testid={`approval-lampiran-${r.id}`}
            >
              <Paperclip size={12} /> {r.lampiran_nama || "Lihat lampiran"}
            </button>
          ) : null}
        </div>
        {actions}
      </div>
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
          Alur Persetujuan
        </div>
        <ApprovalChain chain={r.approval_chain} currentLevel={r.current_level} status={r.status} />
      </div>
    </div>
  );
}

function OvertimeCard({ r, actions }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`ot-approval-card-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <Timer size={11} /> Lembur
            </span>
            <StatusBadge status={r.status} />
            <span className="text-[10px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
              {r.timezone}
            </span>
          </div>
          <div className="mt-2 font-semibold text-slate-900">
            {r.nama} <span className="text-xs font-normal text-slate-500">({r.npp})</span>
          </div>
          <div className="text-sm text-slate-700 mt-0.5">
            {formatTanggalID(r.tanggal)} —{" "}
            <span className="font-mono-clock">{r.rencana_mulai} → {r.rencana_selesai}</span>{" "}
            <span className="text-xs text-slate-400">({r.rencana_jam} jam rencana)</span>
          </div>
          <div className="text-sm text-slate-600 mt-1">{r.alasan}</div>
        </div>
        {actions}
      </div>
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
          Alur Persetujuan
        </div>
        <ApprovalChain chain={r.approval_chain} currentLevel={r.current_level} status={r.status} />
      </div>
    </div>
  );
}

function KlaimCard({ r, actions }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`klaim-approval-card-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <HeartPulse size={11} /> Klaim — {r.kategori_nama}
            </span>
            <StatusBadge status={r.status} />
            {r.jabatan_nama ? (
              <span className="text-[10px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                {r.jabatan_nama}
              </span>
            ) : null}
          </div>
          <div className="mt-2 font-semibold text-slate-900">
            {r.nama} <span className="text-xs font-normal text-slate-500">({r.npp})</span>
          </div>
          <div className="font-mono-clock text-lg font-bold text-teal-800 mt-0.5" data-testid={`klaim-approval-nilai-${r.id}`}>
            {formatRupiah(r.nilai)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Diajukan {formatTanggalID(r.tanggal)}</div>
          {r.keterangan ? (
            <div className="text-sm text-slate-600 mt-1">{r.keterangan}</div>
          ) : null}
          {r.lampiran_path ? (
            <button
              onClick={() => openStoredFile(r.lampiran_path).catch(() => toast.error("Gagal membuka lampiran"))}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-800 hover:text-teal-900"
              data-testid={`klaim-approval-lampiran-${r.id}`}
            >
              <Paperclip size={12} /> {r.lampiran_nama || "Lihat bukti"}
            </button>
          ) : null}
        </div>
        {actions}
      </div>
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
          Alur Persetujuan
        </div>
        <ApprovalChain chain={r.approval_chain} currentLevel={r.current_level} status={r.status} />
      </div>
    </div>
  );
}

export default function Persetujuan() {
  const [pending, setPending] = useState([]);
  const [acted, setActed] = useState([]);
  const [otPending, setOtPending] = useState([]);
  const [otActed, setOtActed] = useState([]);
  const [klPending, setKlPending] = useState([]);
  const [klActed, setKlActed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null); // { id, jenis?, kind: "leave" | "overtime" }
  const [rejectReason, setRejectReason] = useState("");
  const [working, setWorking] = useState(false);

  async function load() {
    try {
      const [p, a, op, oa, kp, ka] = await Promise.all([
        api.get("/requests/pending"),
        api.get("/requests/acted"),
        api.get("/overtime/pending"),
        api.get("/overtime/acted"),
        api.get("/klaim/pending"),
        api.get("/klaim/acted"),
      ]);
      setPending(p.data);
      setActed(a.data);
      setOtPending(op.data);
      setOtActed(oa.data);
      setKlPending(kp.data);
      setKlActed(ka.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function refreshBadge() {
    window.dispatchEvent(new Event("atlas:refresh-approvals"));
  }

  async function approveLeave(r) {
    if (!window.confirm(`Setujui pengajuan ${r.jenis} dari ${r.nama}?`)) return;
    setWorking(true);
    try {
      const { data } = await api.post(`/requests/${r.id}/approve`);
      toast.success(
        data.status === "Disetujui"
          ? "Pengajuan disetujui penuh — status absensi otomatis diperbarui"
          : "Disetujui — diteruskan ke level berikutnya"
      );
      await load();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function approveOvertime(r) {
    if (!window.confirm(`Setujui lembur dari ${r.nama} pada ${formatTanggalID(r.tanggal)}?`)) return;
    setWorking(true);
    try {
      const { data } = await api.post(`/overtime/${r.id}/approve`);
      toast.success(
        data.status === "Disetujui"
          ? "Lembur disetujui penuh — pegawai dapat mencatat waktu aktual"
          : data.perintah_status === "Menunggu Konfirmasi"
            ? "Koordinasi atasan selesai — perintah diteruskan ke Staf untuk diterima"
            : "Disetujui — diteruskan ke atasan berikutnya"
      );
      await load();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function approveKlaim(r) {
    if (!window.confirm(`Setujui klaim ${r.kategori_nama} ${formatRupiah(r.nilai)} dari ${r.nama}?`)) return;
    setWorking(true);
    try {
      const { data } = await api.post(`/klaim/${r.id}/approve`);
      toast.success(
        data.status === "Disetujui"
          ? "Klaim disetujui penuh — saldo plafon pegawai terpotong"
          : "Disetujui — diteruskan ke level berikutnya"
      );
      await load();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function submitReject() {
    if (rejectReason.trim().length < 3) {
      toast.error("Alasan penolakan wajib diisi (min. 3 karakter)");
      return;
    }
    setWorking(true);
    try {
      const path = rejectTarget.kind === "overtime"
        ? `/overtime/${rejectTarget.id}/reject`
        : rejectTarget.kind === "klaim"
          ? `/klaim/${rejectTarget.id}/reject`
          : `/requests/${rejectTarget.id}/reject`;
      await api.post(path, { alasan: rejectReason.trim() });
      toast.success("Pengajuan ditolak");
      setRejectTarget(null);
      setRejectReason("");
      await load();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  const totalPending = pending.length + otPending.length + klPending.length;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-persetujuan">
      <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
        Persetujuan
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
        Pengajuan Menunggu Anda
      </h1>
      <p className="text-slate-500 text-sm mt-1">
        Setujui atau tolak pengajuan bawahan Anda. Total pending: <b>{totalPending}</b>.
      </p>

      <Tabs defaultValue="cuti-pending" className="mt-6">
        <TabsList data-testid="approval-tabs">
          <TabsTrigger value="cuti-pending" data-testid="tab-cuti-pending">
            Cuti & Izin ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="lembur-pending" data-testid="tab-lembur-pending">
            Lembur ({otPending.length})
          </TabsTrigger>
          <TabsTrigger value="klaim-pending" data-testid="tab-klaim-pending">
            Klaim ({klPending.length})
          </TabsTrigger>
          <TabsTrigger value="acted" data-testid="tab-acted">
            Riwayat Tindakan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cuti-pending" className="mt-4 space-y-4">
          {loading ? (
            <div className="text-center text-slate-500 py-14">Memuat…</div>
          ) : pending.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white text-center py-14">
              <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                <ClipboardCheck size={22} />
              </div>
              <div className="mt-3 font-semibold text-slate-700">Tidak ada pengajuan Cuti/Izin menunggu</div>
            </div>
          ) : (
            pending.map((r) => (
              <LeaveCard
                key={r.id}
                r={r}
                actions={
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveLeave(r)}
                      disabled={working}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`approve-btn-${r.id}`}
                    >
                      <Check size={14} /> Setujui
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRejectTarget({ ...r, kind: "leave" }); setRejectReason(""); }}
                      disabled={working}
                      className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                      data-testid={`reject-btn-${r.id}`}
                    >
                      <X size={14} /> Tolak
                    </Button>
                  </div>
                }
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="lembur-pending" className="mt-4 space-y-4">
          {loading ? (
            <div className="text-center text-slate-500 py-14">Memuat…</div>
          ) : otPending.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white text-center py-14">
              <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                <Timer size={22} />
              </div>
              <div className="mt-3 font-semibold text-slate-700">Tidak ada pengajuan lembur menunggu</div>
            </div>
          ) : (
            otPending.map((r) => (
              <OvertimeCard
                key={r.id}
                r={r}
                actions={
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveOvertime(r)}
                      disabled={working}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`ot-approve-btn-${r.id}`}
                    >
                      <Check size={14} /> Setujui
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRejectTarget({ ...r, kind: "overtime" }); setRejectReason(""); }}
                      disabled={working}
                      className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                      data-testid={`ot-reject-btn-${r.id}`}
                    >
                      <X size={14} /> Tolak
                    </Button>
                  </div>
                }
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="klaim-pending" className="mt-4 space-y-4">
          {loading ? (
            <div className="text-center text-slate-500 py-14">Memuat…</div>
          ) : klPending.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white text-center py-14">
              <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                <HeartPulse size={22} />
              </div>
              <div className="mt-3 font-semibold text-slate-700">Tidak ada klaim pengobatan menunggu</div>
            </div>
          ) : (
            klPending.map((r) => (
              <KlaimCard
                key={r.id}
                r={r}
                actions={
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveKlaim(r)}
                      disabled={working}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`klaim-approve-btn-${r.id}`}
                    >
                      <Check size={14} /> Setujui
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRejectTarget({ ...r, kind: "klaim" }); setRejectReason(""); }}
                      disabled={working}
                      className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                      data-testid={`klaim-reject-btn-${r.id}`}
                    >
                      <X size={14} /> Tolak
                    </Button>
                  </div>
                }
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="acted" className="mt-4 space-y-4">
          {acted.length === 0 && otActed.length === 0 && klActed.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white text-center py-14 text-sm text-slate-500">
              Belum ada riwayat tindakan.
            </div>
          ) : (
            <>
              {acted.map((r) => <LeaveCard key={`l-${r.id}`} r={r} actions={null} />)}
              {otActed.map((r) => <OvertimeCard key={`o-${r.id}`} r={r} actions={null} />)}
              {klActed.map((r) => <KlaimCard key={`k-${r.id}`} r={r} actions={null} />)}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Tolak Pengajuan</DialogTitle>
            <DialogDescription>
              {rejectTarget
                ? rejectTarget.kind === "overtime"
                  ? `Lembur — ${rejectTarget.nama} — ${formatTanggalID(rejectTarget.tanggal)}`
                  : rejectTarget.kind === "klaim"
                    ? `Klaim ${rejectTarget.kategori_nama} — ${rejectTarget.nama} — ${formatRupiah(rejectTarget.nilai)}`
                    : `${rejectTarget.jenis} — ${rejectTarget.nama}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Alasan Penolakan (wajib)
            </Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              placeholder="Tuliskan alasan penolakan…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="input-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} data-testid="reject-cancel-btn">
              Batal
            </Button>
            <Button
              onClick={submitReject}
              disabled={working}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="reject-confirm-btn"
            >
              {working ? "Memproses…" : "Tolak Pengajuan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
